/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Countdown, { CountdownRendererFn } from 'react-countdown';
import { useForm } from 'react-hook-form';
import ReactCodeInput from 'react-code-input';

import mailImg from '../../../assets/auth/mail.svg';
import { Button, Input } from '../../../components';
import {
  useGetAuthConfigurationQuery,
  useForgetPasswordMutation,
  useResetPasswordMutation,
  useVerifyAccountMutation,
  useLoginMutation,
} from '../../../services';
import { Login } from '../../../models';
// core-api signals reset-flow failures INSIDE the AppResponse body (HTTP 200
// with errorCode !== '0'), so `.unwrap()` does not reject on them; it can also
// surface a transport-level error. These helpers — formerly module-local copies
// living right here — normalise both shapes and are now the repo-canonical
// versions. See src/utils/api-response.ts for the envelope contract and for why
// the errorCode dialect, not the companies pages' httpStatus dialect, decides
// body-level business outcomes.
import {
  MutationResult,
  classifyMutationResult,
  extractResponsePayload,
  isSuccess,
} from '../../../utils/api-response';

// The ONE business marker that means the mail never left the server:
// AuthService.generateOTP catches the send failure and answers with
// GenerateExceptionAppResponse.prepareCustomErrorAppResponse('EMAIL FAILED',
// <raw mailer text>), i.e. HTTP 200 carrying message 'EMAIL FAILED',
// errorCode '1030', details <raw mailer text> and data ''.
//
// The marker rides in `message`, NOT in `errorCode` — errorCode carries the
// generic CUSTOM_ERROR code 1030 that EVERY custom error in that backend
// shares (an invalid or expired reset token returns 1030 too), so matching on
// the code would misreport unrelated failures as delivery failures. Compare
// this constant against payload.message and nothing else.
//
// It stays page-local on purpose: it is a business marker for THIS flow, not
// part of the envelope contract in src/utils/api-response.ts.
const EMAIL_FAILED_MESSAGE = 'EMAIL FAILED';

// Every user-facing failure string of this flow, in one place, and every one of
// them FIXED and outcome-independent on purpose.
//
// The verify and reset handlers used to do `setErrorMessage(payload.details ||
// payload.message || <fallback>)`, which piped raw server text straight onto an
// ANONYMOUS caller's screen — a JDK exception message, or the backend's
// token-state discriminator that separates "no such token" from "already
// consumed" from "expired". Those two handlers now do not read the response
// body on their failure path AT ALL, so there is no route by which server text
// can reach the DOM. The strings below are byte-identical to the fallbacks that
// used to sit in third position, so what a user sees on a generic failure is
// unchanged; only the details/message fallthrough is gone.
const SEND_CODE_FAILED_MESSAGE =
  'We could not send the verification code right now. Please try again in a moment.';
const VERIFY_CODE_FAILED_MESSAGE =
  'That code is invalid or has expired. Please try again.';
const RESET_PASSWORD_FAILED_MESSAGE =
  'The password reset link is invalid or has expired. Please request a new code.';

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.length > 0;

/**
 * Same predicate, same reasoning as the login page's (kept page-local in both
 * places rather than sharing a util, because the only shared module either page
 * imports is the envelope contract and that file deliberately knows nothing
 * about the session payload).
 *
 * A body-level SUCCESS is not proof of a usable session: the `data` slot of an
 * errorCode '0' envelope can still be `''`, `null` or half-built, and the
 * previous auto-login destructured it blindly — persisting the literal string
 * 'undefined' as the session, or throwing a TypeError inside a `setTimeout`
 * callback (unhandled, and with the user parked on the success screen forever).
 * FAILS CLOSED: an unknown shape is a failed auto-login.
 */
const isCompleteSession = (value: unknown): value is Login => {
  if (!value || typeof value !== 'object') return false;

  const session = value as Partial<Login>;

  return (
    isNonEmptyString(session.accessToken) &&
    isNonEmptyString(session.refreshToken) &&
    isNonEmptyString(session.userName)
  );
};

export const ForgetPassword = () => {
  const { state } = useLocation() as { state: string };

  const [content, setContent] = useState<
    'forgetPassword' | 'verifyAccount' | 'resetPassword' | 'passwordChanged'
  >('forgetPassword');

  const [isPasswordMatch, setIsPasswordMatch] = useState(true);
  // Single-use reset token returned by verifyAccount — the only proof of a
  // verified OTP that resetPassword accepts. Held in component state only;
  // it is not a session and must never reach the auth slice or localStorage.
  const [resetToken, setResetToken] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const navigate = useNavigate();
  const [forgetPassword, { isLoading: isForgetPasswordLoading }] =
    useForgetPasswordMutation();
  const [verifyAccount, { isLoading: isVerifyAccountLoading }] =
    useVerifyAccountMutation();
  const [resetPassword, { isLoading: isResetPasswordLoading }] =
    useResetPasswordMutation();
  const [login] = useLoginMutation();
  const { data } = useGetAuthConfigurationQuery();

  const emailValidation = data?.data?.EMAIL_LOGIN_REGEX?.value;

  const {
    register,
    formState: { errors },
    getValues,
    setValue,
    handleSubmit,
    setFocus,
  } = useForm<{
    email: string;
    otp: string;
    password: string;
    confirmPassword: string;
  }>({
    defaultValues: {
      email: state,
    },
  });

  // Sole call site of the forgetPassword mutation — both the Continue button
  // and the Resend link go through here, so a failed resend surfaces exactly
  // like a failed first send.
  //
  // ONLY a delivery failure stops the flow. Every other body-level failure —
  // notably the invalid-credentials response (errorCode 1027) returned for an
  // address with no account — advances to the OTP step identically to a
  // success, which is what keeps this endpoint from becoming an
  // account-enumeration oracle. The single-marker shape is deliberate: a new
  // backend failure added later defaults to advancing. Note that errorCode on
  // its own cannot identify the outcome here, because 1030 (CUSTOM_ERROR) is
  // shared by every custom error including an invalid reset token — the
  // delivery failure is recognised by its `message`.
  //
  // Returns whether the code was actually sent (used by the resend cooldown).
  const requestCode = async (): Promise<boolean> => {
    // Button.tsx exposes no disabled state (click lives on a wrapping div), so
    // re-entrancy is guarded in-handler until that component gets its own fix.
    if (isForgetPasswordLoading) return false;
    setErrorMessage('');

    const response = await forgetPassword({ email: getValues('email') });

    const payload = extractResponsePayload(response);
    // TRANSPORT_FAILURE + BODY_FAILURE are exactly the complement of SUCCESS,
    // so this is the same predicate as the previous
    // `!isSuccess(response) && (isTransportError || message === marker)`.
    const outcome = classifyMutationResult(response);
    const deliveryFailed =
      outcome === MutationResult.TRANSPORT_FAILURE ||
      (outcome === MutationResult.BODY_FAILURE &&
        payload.message === EMAIL_FAILED_MESSAGE);

    if (deliveryFailed) {
      // Fixed, address-independent copy on purpose: on EMAIL FAILED the backend
      // puts the raw SMTP exception text in payload.details, so surfacing it
      // would widen an information leak and could vary by address. `payload` is
      // read here ONLY to match the marker, never to build this string.
      setErrorMessage(SEND_CODE_FAILED_MESSAGE);
      return false;
    }

    setContent('verifyAccount');
    return true;
  };

  const onVerifyAccount = async () => {
    if (isVerifyAccountLoading) return;
    setErrorMessage('');
    const response = await verifyAccount({
      email: getValues('email'),
      otp: getValues('otp'),
    });

    if (isSuccess(response) && 'data' in response) {
      setResetToken(response.data.data.resetToken);
      setContent('resetPassword');
      return;
    }

    // Rejected OTP (wrong / expired / never issued): one fixed string for every
    // failure. The body is deliberately NOT extracted here — its details and
    // message carry raw server text and would tell an anonymous caller which of
    // those cases they hit.
    setErrorMessage(VERIFY_CODE_FAILED_MESSAGE);
  };

  // Reset submit takes NO parameter on purpose: handleSubmit would otherwise
  // hand over the whole form object, and only email + password + resetToken may
  // reach the request body (never otp or confirmPassword).
  const onSubmit = async () => {
    const matched = getValues('password') === getValues('confirmPassword');
    setIsPasswordMatch(matched);
    if (!matched) return setFocus('confirmPassword');

    // The reset token is single-use; block a double submit from burning it.
    if (isResetPasswordLoading) return;

    setErrorMessage('');
    const response = await resetPassword({
      email: getValues('email'),
      password: getValues('password'),
      resetToken,
    });

    if (isSuccess(response)) {
      setContent('passwordChanged');

      // TODO: add lotti animation
      setTimeout(async () => {
        // The whole convenience auto-login is inside try/catch: this runs in a
        // detached timer callback, so anything thrown here is an unhandled
        // rejection that leaves the admin parked on the success screen with no
        // navigation and no way forward.
        try {
          const loginResponse = await login({
            email: getValues('email'),
            password: getValues('password'),
          });

          if (isSuccess(loginResponse) && 'data' in loginResponse) {
            // Same guard as the login page: only a COMPLETE triple becomes a
            // session. A success-coded reply with `data: null` used to throw
            // here, and a half-built one used to persist the string
            // 'undefined' — on an account whose password had just changed.
            const session: unknown = loginResponse.data.data;

            if (isCompleteSession(session)) {
              localStorage.setItem('accessToken', session.accessToken);
              localStorage.setItem('refreshToken', session.refreshToken);
              localStorage.setItem('userName', session.userName);

              navigate('/');
              return;
            }
          }
        } catch {
          // Fall through to the login page — see below.
        }

        // The password DID change; only the convenience auto-login failed, and
        // NOTHING was persisted on the way here. Land the admin on the login
        // form, which is usable and states the next step, rather than leaving
        // them on a success screen that never moves.
        navigate('/login');
      }, 3000);
      return;
    }

    // Rejected reset (invalid / expired / already-used token): show a visible
    // error and send the admin back to the request-code step. The token is
    // spent server-side either way, so drop it here too. One fixed string for
    // all three token states — the body is NOT extracted, because its details
    // and message are exactly what distinguishes them.
    setResetToken('');
    setErrorMessage(RESET_PASSWORD_FAILED_MESSAGE);
    setContent('forgetPassword');
  };

  const errorAlert = errorMessage ? (
    <div
      role="alert"
      className="mb-5 rounded-lg border border-error bg-error/10 px-4 py-3 text-sm text-error-600"
    >
      {errorMessage}
    </div>
  ) : null;

  const renderer: CountdownRendererFn = ({ seconds, completed, api }) => {
    if (completed) {
      return (
        <button
          className="text-primary"
          onClick={() => {
            void (async () => {
              const sent = await requestCode();
              // Only restart the 59s cooldown when the code actually went out.
              // On a failed resend the Resend link stays clickable so the admin
              // can retry immediately instead of waiting on a send that never
              // happened; the error banner on this screen explains why.
              if (!sent) return;
              api.stop();
              api.start();
            })();
          }}
        >
          Resend
        </button>
      );
    } else {
      return <span className="text-primary">00:{seconds} Sec</span>;
    }
  };

  if (content === 'forgetPassword') {
    return (
      <div className="mx-auto mt-32 max-w-sm 2xl:mt-60">
        <h2 className="mb-3 text-center text-2xl font-bold text-secondary 2xl:text-3xl">
          Forgot password?
        </h2>
        <p className="mb-7 text-center text-gray-500">
          Please enter the email address that you should receive the code on.
        </p>
        <div>
          {errorAlert}
          <div className="mb-5">
            <Input
              required
              type="text"
              name="email"
              id="email"
              label="Email"
              placeholder="Enter your email"
              register={register}
              prefixIcon={mailImg}
              isError={errors.email?.type === 'pattern'}
              pattern={
                new RegExp(
                  emailValidation
                    ? emailValidation.slice(1, emailValidation.length - 1)
                    : '',
                  'g'
                )
              }
            />
          </div>

          <div className="mt-6 w-full" onClick={() => void requestCode()}>
            <Button type="button">
              {isForgetPasswordLoading ? 'Sending…' : 'Continue'}
            </Button>
          </div>

          <p className="mt-4 text-center text-xs text-secondary">
            Remember your password?{' '}
            <Link className="text-primary" to="/login">
              Back to login
            </Link>
          </p>
        </div>
      </div>
    );
  } else if (content === 'verifyAccount') {
    return (
      <div className="mx-auto mt-32 max-w-sm 2xl:mt-60">
        <h2 className="mb-3 text-center text-2xl font-bold text-secondary 2xl:text-3xl">
          Verify account
        </h2>
        <p className="mb-7 text-center text-gray-500">
          We’ve sent a 6-digit verification code to{' '}
          {getValues('email') || state}. Please check your email.
        </p>
        <div>
          {errorAlert}
          <div className="mb-5">
            <ReactCodeInput
              placeholder="*"
              type="text"
              fields={6}
              inputMode="numeric"
              name="otp"
              onChange={value => setValue('otp', value)}
            />
          </div>

          <div className="mt-6 w-full" onClick={() => void onVerifyAccount()}>
            <Button type="button">
              {isVerifyAccountLoading ? 'Verifying…' : 'Verify'}
            </Button>
          </div>

          <p className="mt-4 text-center text-xs text-secondary">
            Don’t receive code?{' '}
            <Countdown
              date={Date.now() + 59000}
              autoStart={true}
              renderer={renderer}
            />
          </p>
        </div>
      </div>
    );
  } else if (content === 'resetPassword') {
    return (
      <div className="mx-auto mt-32 max-w-sm 2xl:mt-60">
        <h2 className="mb-3 text-center text-2xl font-bold text-secondary 2xl:text-3xl">
          Reset password{' '}
        </h2>
        <p className="mb-7 text-center text-gray-500">
          Now you should be able to enter a new password for your account.{' '}
        </p>
        <form onSubmit={handleSubmit(onSubmit)}>
          {errorAlert}
          <div>
            <Input
              required
              type="password"
              name="password"
              id="password"
              label="New password"
              register={register}
            />
          </div>

          <div>
            <Input
              required
              type="password"
              name="confirmPassword"
              id="confirmPassword"
              label="Confirm new password"
              register={register}
            />
          </div>

          {!isPasswordMatch && (
            <p className="mt-4 text-xs text-error-600">
              The password confirmation does not match
            </p>
          )}

          <div className="mt-6 w-full">
            <Button type="submit">
              {isResetPasswordLoading ? 'Resetting…' : 'Reset Password'}
            </Button>
          </div>
        </form>
      </div>
    );
  } else {
    return (
      <div className="mx-auto mt-32 max-w-lg 2xl:mt-60">
        <h2 className="mb-3 text-center text-2xl font-bold text-secondary 2xl:text-3xl">
          Password successfully changed!{' '}
        </h2>
        <p className="mb-7 text-center text-gray-500">
          Your password is changed successfully and you will be automatically
          logged in in seconds.
        </p>
      </div>
    );
  }
};
